import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { KeyRound, Loader2, CheckCircle, ArrowRight, Sparkles } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { toast } from "sonner";
import axios from "axios";
import { API } from "../App";

const BetaRedeem = () => {
  const navigate = useNavigate();
  const { code: codeFromUrl } = useParams();
  const [code, setCode] = useState((codeFromUrl || "").toUpperCase());
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);
  const [autoTried, setAutoTried] = useState(false);

  // If a code is in the URL, try redeeming automatically on first load
  useEffect(() => {
    if (codeFromUrl && !autoTried) {
      setAutoTried(true);
      handleRedeem(codeFromUrl.toUpperCase());
    }
  }, [codeFromUrl, autoTried]);

  const handleRedeem = async (codeOverride) => {
    const useCode = (codeOverride || code).trim().toUpperCase();
    if (!useCode) {
      toast.error("Please enter your beta code");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/beta/redeem`, { code: useCode });
      setSuccess(r.data);
      toast.success("Beta access unlocked.");
      // Hard navigation (not React Router) so the PrelaunchGate's
      // useLaunchMode hook refetches /api/launch/mode and sees the
      // newly-set dhdh_beta_access cookie before /beta-launch is rendered.
      // A SPA `navigate()` would race the cookie-vs-state load and end up
      // back on the prelaunch waitlist.
      setTimeout(() => { window.location.assign("/beta-launch"); }, 1800);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not redeem this code");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#FAF7F2] flex items-center justify-center p-6 relative overflow-hidden" data-testid="beta-redeem-page">
      <div className="absolute -top-40 -right-40 w-96 h-96 bg-[#8DB5A0]/10 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute -bottom-40 -left-40 w-96 h-96 bg-[#8DB5A0]/10 rounded-full blur-3xl pointer-events-none" />

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="relative w-full max-w-md"
      >
        <div className="bg-white border border-[#E5DFEC] rounded-3xl p-8 shadow-sm">
          <div className="w-12 h-12 rounded-xl bg-[#8DB5A0]/10 flex items-center justify-center mb-5">
            <KeyRound className="w-6 h-6 text-[#8DB5A0]" />
          </div>

          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              data-testid="beta-redeem-success"
            >
              <CheckCircle className="w-10 h-10 text-[#10B981] mb-4" />
              <h1 className="text-2xl font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                Welcome{success.invitee_name ? `, ${success.invitee_name.split(" ")[0]}` : ""}!
              </h1>
              <p className="text-sm text-[#475569] mb-4">
                Your beta access is unlocked. As a founding beta tester you've locked in <strong className="text-[#8DB5A0]">RDH Pro at $19/mo or $199/yr — for life</strong>. Sending you to the launch overview…
              </p>
              <div className="flex items-center gap-2 text-xs text-[#64748B]">
                <Loader2 className="w-3 h-3 animate-spin" />
                Loading your launch experience…
              </div>
            </motion.div>
          ) : (
            <>
              <h1 className="text-2xl sm:text-3xl font-medium text-[#6B4E8A] mb-2" style={{ fontFamily: "Outfit, sans-serif" }}>
                Enter your beta code
              </h1>
              <p className="text-sm text-[#64748B] mb-6 leading-relaxed">
                You've been invited to test DHDH before public launch. Enter the 6-character code from your invite email below.
              </p>

              <div className="bg-[#FAF7F2] border border-[#E5DFEC] rounded-xl p-4 mb-5">
                <div className="flex items-center gap-2 mb-1">
                  <Sparkles className="w-3.5 h-3.5 text-[#8DB5A0]" />
                  <p className="text-xs font-bold uppercase tracking-wider text-[#6B4E8A]">Founding beta perk</p>
                </div>
                <p className="text-xs text-[#475569] leading-relaxed">
                  Beta testers automatically lock in the <strong>RDH Pro founding rate ($19/mo or $199/yr) for life</strong> — no expiry, no cap.
                </p>
              </div>

              <div className="space-y-3">
                <Input
                  type="text"
                  placeholder="ABC123"
                  value={code}
                  onChange={(e) => setCode(e.target.value.toUpperCase())}
                  onKeyDown={(e) => e.key === "Enter" && handleRedeem()}
                  disabled={submitting}
                  maxLength={10}
                  className="text-center text-2xl font-mono tracking-[0.3em] py-6 bg-white border-[#E5DFEC] uppercase"
                  data-testid="beta-code-input"
                  autoFocus
                />
                <Button
                  onClick={() => handleRedeem()}
                  disabled={submitting || !code.trim()}
                  className="w-full bg-[#6B4E8A] text-white hover:bg-[#553D6F] rounded-xl py-5 font-medium"
                  data-testid="beta-redeem-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Verifying...</>
                  ) : (
                    <>Redeem & continue <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>

              <button
                onClick={() => navigate("/")}
                className="text-xs text-[#94A3B8] hover:text-[#6B4E8A] transition-colors mt-5 mx-auto block"
                data-testid="beta-back-link"
              >
                ← Don't have a code? Join the waitlist instead
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default BetaRedeem;
